import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { map } from 'rxjs/operators'
import { Observable, throwError } from 'rxjs'
import { environment } from '../../environments/environment'
import { ISortingModel } from "../models/sorting.model"
import { IPaginationModel } from "../models/pagination.model"
import { IQueryableFilter } from "../filters/queryable.filter"
import { ComplexDatepickerService } from "../components/complex-datepicker/complex-datepicker.service"
import { HelperService } from "./helper.service"
import { ComplexDatepickerFilter } from "../components/complex-datepicker/complex-datepicker.filter"
import { UserGroupsEnum } from '../enums/user-groups.enum'

export interface IDOptions {
  downloadPath: string
  resourceName?: string
  reportFileName?: string
}

export class ExcelOptions implements IDOptions {
  public downloadPath: string
  public resourceName: string = 'base64'
  public reportFileName: string = 'report.xlsx'

  constructor(model?: IDOptions) {
    if (model) {
      Object.assign(this as Object, model)
    }
  }
}

export class PdfOptions implements IDOptions {
  public downloadPath: string
  public resourceName: string = 'base64'
  public reportFileName: string = 'report.pdf'

  constructor(model?: IDOptions) {
    if (model) {
      Object.assign(this as Object, model)
    }
  }
}

export interface IManualOptions {
  manualTye: number
}

const sameFormatsRoles = (roles: string[]) => ({ excel: roles, pdf: roles });

const ALL_LEADS = [
  UserGroupsEnum.SPAS_Admin.toString(),
  UserGroupsEnum.SPAS_CBMLead.toString(),
  UserGroupsEnum.SPAS_RCALead.toString(),
];

const ALL_USERS = [
  ...ALL_LEADS,
  UserGroupsEnum.SPAS_Operator.toString(),
  UserGroupsEnum.SPAS_Guest.toString(),
];

const ACCESSIBLE_ROLES = {
  'final-element-analyzer': sameFormatsRoles(ALL_LEADS),
  'shutdown-analyzer': sameFormatsRoles([
    UserGroupsEnum.SPAS_Admin.toString(),
    UserGroupsEnum.SPAS_CBMLead.toString(),
  ]),
  'safe-state-limit-switch': sameFormatsRoles(ALL_LEADS),
  'event-viewer': sameFormatsRoles([
    UserGroupsEnum.SPAS_Admin.toString(),
    UserGroupsEnum.SPAS_CBMLead.toString(),
  ]),
  'raw-event-viewer': sameFormatsRoles([
    ...ALL_LEADS,
    UserGroupsEnum.SPAS_Configurator.toString(),
  ]),
  'fault-statistics': {
    'final-element': sameFormatsRoles(ALL_USERS),
    'initiator': sameFormatsRoles(ALL_USERS),
  },
  'final-element-activations': sameFormatsRoles(ALL_USERS),
  'final-element-overview': sameFormatsRoles(ALL_USERS),
  'final-elements': {
    'non-operational-equipment': sameFormatsRoles(ALL_USERS),
  },
  'travel-time-monitoring': sameFormatsRoles(ALL_USERS),
  'initiators': {
    'process-demands': sameFormatsRoles(ALL_USERS),
    'initiator-faults': sameFormatsRoles(ALL_USERS),
    'fg-demands': sameFormatsRoles(ALL_USERS),
  },
  'overrides': {
    'override-tracker': sameFormatsRoles(ALL_USERS),
  },
  'proof-test': {
    'overview': sameFormatsRoles(ALL_USERS),
    'approval': sameFormatsRoles(ALL_USERS),
    'program-verification': sameFormatsRoles(ALL_USERS),
  },
  'shutdown': sameFormatsRoles(ALL_USERS),
  'sif-manual': {
    'overview': sameFormatsRoles(ALL_USERS),
    'logic-solver-response-time': sameFormatsRoles(ALL_USERS),
    'status-tracker': {
      'default': sameFormatsRoles(ALL_USERS),
      'tracker': sameFormatsRoles(ALL_USERS),
    },
  },
  'user-management': {
    'app': {
      'user': {
        'excel': [UserGroupsEnum.SPAS_Admin.toString()],
      }
    }
  },
};

export interface IDownloadableQuery {
  filter?: IQueryableFilter,
  sort?: ISortingModel,
  pagination?: IPaginationModel,
  [key: string]: any
}

@Injectable({
  providedIn: 'root',
})
export class DownloaderService {
  public static TYPE_EXCEL: string = 'EXCEL'
  public static TYPE_PDF: string = 'PDF'

  private apiURL: string = environment.apiUrl
  private _excelOptions: ExcelOptions
  private _pdfOptions: PdfOptions
  private _manualOptions: IManualOptions
  private _queryString: string
  private _query: IDownloadableQuery

  public canDownloadReportsByRole(pathString: string, userRole: string | null): boolean {
    const segments = pathString.split('/').filter(Boolean);
  
    if (!userRole || segments.length < 2) {
      return false;
    }

    const lastSegment = segments.pop()!;
    const format = lastSegment.includes('excel') ? 'excel' : lastSegment.includes('pdf') ? 'pdf' : null;

    if (!format) return false;

    let currentLevel: any = ACCESSIBLE_ROLES;

    for (const segment of segments) {
      if (currentLevel && typeof currentLevel === 'object' && segment in currentLevel) {
        currentLevel = currentLevel[segment];
      } else {
        return false; 
      }
    }

    const allowedRoles: string[] = currentLevel?.[format];
    if (!Array.isArray(allowedRoles)) return false;

    return allowedRoles.indexOf(userRole) !== -1;
  }

  public hasExcel(userRole: string | null): boolean {
    return this._excelOptions &&
           this._excelOptions.downloadPath !== '' &&
           this.canDownloadReportsByRole(this._excelOptions.downloadPath, userRole)
  }

  public hasPdf(userRole: string | null): boolean {
    return this._pdfOptions &&
           this._pdfOptions.downloadPath !== '' &&
           this.canDownloadReportsByRole(this._pdfOptions.downloadPath, userRole)
  }

  public hasManual(): boolean {
    return this._manualOptions && !!this._manualOptions.manualTye
  }

  public get queryString(): string {
    return this._queryString
  }

  public set queryString(queryString: string) {
    this._queryString = queryString
  }

  get query(): IDownloadableQuery {
    return this._query
  }

  public set query(query: IDownloadableQuery) {
    this._query = query
  }

  constructor(
    private http: HttpClient,
    private datePickerService: ComplexDatepickerService
  ) {}

  public init(options: { excel?: ExcelOptions; pdf?: PdfOptions; manual?: IManualOptions }): void {
    if (options.excel) {
      this._excelOptions = options.excel
    }
    if (options.pdf) {
      this._pdfOptions = options.pdf
    }
    if (options.manual) {
      this._manualOptions = options.manual
    }
  }

  public getHeaders(): HttpHeaders {
    let headers: HttpHeaders = new HttpHeaders()
    headers = headers.set('Content-Type', 'application/json')
    return headers
  }

  public download(type: string): Observable<any> {
    let url: string = this.apiURL
    let resKey: string
    if (type === DownloaderService.TYPE_EXCEL && this._excelOptions) {
      url =
        this.apiURL +
        this._excelOptions.downloadPath +
        this.getQueryString()
      resKey = this._excelOptions.resourceName
    } else if (type === DownloaderService.TYPE_PDF && this._pdfOptions) {
      url =
        this.apiURL +
        this._pdfOptions.downloadPath +
        this.getQueryString()
      resKey = this._pdfOptions.resourceName
    } else {
      throw throwError( () => new Error('Invalid configuration.') )
    }

    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: any) => {
        return resource[resKey]
      }),
    )
  }

  private getQueryString(): string {
    if (this._query) {
      const query: any = {...this._query}

      if (this._query.filter) {
        query.filter = this._query.filter instanceof ComplexDatepickerFilter ?
          this.datePickerService.toJsonFilterWithDates(this._query.filter) :
          this._query.filter.toJson()
      }

      return '?' + HelperService.complexObjectToQuery(query)
    }

    if (this._queryString) {
      return '?' + this._queryString
    }

    return ''
  }

  public downloadManual() {
    const url: string =
      this.apiURL +
      '/user-manual/' +
      encodeURIComponent(this._manualOptions.manualTye) +
      '/download/file'
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: { status: string; base64: any; fileType: string; fileName: string }) => {
        return resource
      }),
    )
  }

  public getReportName(type: string): string {
    if (type === DownloaderService.TYPE_EXCEL) {
      return this._excelOptions ? this._excelOptions.reportFileName : 'report.xlsx'
    } else if (type === DownloaderService.TYPE_PDF) {
      return this._pdfOptions ? this._pdfOptions.reportFileName : 'report.pdf'
    }
    return ''
  }

  public reset(): void {
    this._excelOptions = null
    this._pdfOptions = null
    this._manualOptions = null
    this._queryString = null
    this._query = null
  }

  public downloadAsBlob(type: string) {
    let url: string = this.apiURL
    if (type === DownloaderService.TYPE_EXCEL && this._excelOptions) {
      url = this.apiURL + this._excelOptions.downloadPath + '?' + this._queryString
    } else if (type === DownloaderService.TYPE_PDF && this._pdfOptions) {
      url = this.apiURL + this._pdfOptions.downloadPath + '?' + this._queryString
    } else {
      throw throwError( () => new Error('Invalid configuration.') )
    }

    return this.http.get(url, { responseType: 'blob' })
  }
}
